import { inject, Injectable } from '@angular/core';
import { from, map, of, switchMap, throwError } from 'rxjs';
import { loadScript } from '../lib/load-script';
import { PICFLOW_CONFIG } from '../picflow-config';
import { NetworkInterceptorHelperService } from './network-interceptor-helper.service';
import { SentryService } from './sentry.service';

declare global {
  interface Window {
    AwsWafIntegration: {
      fetch: typeof fetch;
      forceRefreshToken: () => Promise<void>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class WafHttpService {
  private config = inject(PICFLOW_CONFIG);
  private networkInterceptorHelper = inject(NetworkInterceptorHelperService);
  private sentryService = inject(SentryService);

  public post(url: string, params: unknown) {
    const wafUrl = this.config.wafEndpoint;
    return this.loadWafScript(wafUrl).pipe(
      switchMap(() => {
        const tenantHeaders = this.networkInterceptorHelper.getTenantHeaders();
        return this.networkInterceptorHelper.getUserHeaders().pipe(
          map(userHeaders => {
            return { ...(tenantHeaders || {}), ...(userHeaders || {}) };
          })
        );
      }),
      switchMap(headers =>
        from(
          window.AwsWafIntegration.fetch(url, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
              ...headers,
              // content type: json
              'Content-Type': 'application/json',
            },
          })
        ).pipe(
          switchMap(response => {
            if (response.status === 204) {
              // 204 means no content
              return of(null);
            }
            if (response.status === 202) {
              this.sentryService.captureException('WAF Challenge');
              return throwError(() => 'waf-challenge');
            }
            return from(response.json());
          })
        )
      )
    );
  }

  private loadWafScript(url: string) {
    return from(loadScript(url)).pipe(switchMap(() => from(window.AwsWafIntegration.forceRefreshToken())));
  }
}
